import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useForm, Controller } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { z } from 'zod';
import { useParams, useNavigate, Link } from 'react-router-dom';
import { customers } from '@/api/customers';
import { customBilling, UNIT_KIND_LABELS } from '@/api/customBilling';
import type { BillableItemRow, PricingRuleRow } from '@/api/customBilling';
import { featureFlags, type EffectiveFeatureRow } from '@/api/featureFlags';
import { useCurrentUser } from '@/hooks/useCurrentUser';
import { PageHeader } from '@/components/PageHeader';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import {
  Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle,
} from '@/components/ui/dialog';
import { Switch } from '@/components/ui/switch';
import { Pencil, UserPlus } from 'lucide-react';
import { BackupConfigCard } from '@/components/BackupConfigCard';
import { RecordingAdminsCard } from '@/components/recordingAdmins/RecordingAdminsCard';
import { PhoneRecordingSettingsCard } from '@/components/recordingAdmins/PhoneRecordingSettingsCard';
import { TimezonePicker } from '@/components/TimezonePicker';
import { CountryPrefixSelect } from '@/components/PhoneInput';
import { formatRef } from '@/lib/formatRef';

const editSchema = z.object({
  name: z.string().min(1, 'Name is required'),
  timezone: z.string().min(1, 'Timezone is required'),
  language: z.enum(['en', 'he', 'yi']),
  // '+' + 1-3 digits, or null = inherit the reseller default (migration 0113).
  defaultCountryCode: z.string().regex(/^\+[1-9]\d{0,2}$/, 'Invalid country code').nullable(),
});
type EditValues = z.infer<typeof editSchema>;

// LANGUAGES const removed 2026-08-06 — the edit dialog's language selector is
// hidden until he/yi translations ship (see the commented block in
// EditCustomerDialog + docs/backlog.md). Restore it together with the select.

export function CustomerDetail() {
  const { t } = useTranslation();
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const qc = useQueryClient();
  const currentUser = useCurrentUser();
  const [editOpen, setEditOpen] = useState(false);
  const [editError, setEditError] = useState<string | null>(null);

  // `id` existence guard is below; mutationFn closures rely on UI-level guarding.
  const { data, isLoading, error } = useQuery({
    queryKey: ['customer', id],
    queryFn: () => customers.get(id!),
    enabled: Boolean(id),
  });
  const toggle = useMutation({
    mutationFn: () => customers.update(id!, { active: !data!.active }),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['customer', id] }),
  });
  const del = useMutation({
    mutationFn: () => customers.remove(id!),
    onSuccess: () => navigate('/customers'),
  });
  const update = useMutation({
    mutationFn: (v: EditValues) =>
      customers.update(id!, {
        name: v.name,
        timezone: v.timezone,
        language: v.language,
        defaultCountryCode: v.defaultCountryCode,
      }),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['customer', id] });
      qc.invalidateQueries({ queryKey: ['customers'] });
      // Phone fields cache the resolved default prefix — refresh it so a
      // changed default country code shows up without a reload.
      qc.invalidateQueries({ queryKey: ['phoneDefaults'] });
      setEditOpen(false);
    },
  });

  if (!id) return <div className="text-sm text-destructive">{t('common.invalidUrl')}</div>;
  if (isLoading) return <div className="text-sm text-muted-foreground">{t('common.loading')}</div>;
  if (error || !data) return <div className="text-sm text-destructive">{error instanceof Error ? error.message : t('common.notFound')}</div>;

  function inviteUserHref() {
    return `/users/new?customerId=${id}&resellerId=${data!.reseller_id}`;
  }

  return (
    <>
      <PageHeader
        eyebrow={`Customer · #${formatRef(data.customer_number)}`}
        title={data.name}
        description={`${t('customer.customerNumber')} ${formatRef(data.customer_number)}`}
        actions={
          <>
            <Button variant="outline" onClick={() => { setEditError(null); setEditOpen(true); }}>
              <Pencil className="h-4 w-4" /> {t('common.edit')}
            </Button>
            <Button asChild>
              <Link
                to={inviteUserHref()}
                state={{
                  customerId: id,
                  resellerId: data.reseller_id,
                  customerName: data.name,
                }}
              >
                <UserPlus className="h-4 w-4" /> {t('customer.inviteUser')}
              </Link>
            </Button>
          </>
        }
      />

      <div className="grid gap-6 md:grid-cols-2">
        <Card>
          <CardHeader>
            <CardTitle className="text-base">{t('common.details')}</CardTitle>
          </CardHeader>
          <CardContent>
            <dl className="grid grid-cols-[auto_1fr] gap-x-6 gap-y-3 text-sm">
              <dt className="text-muted-foreground">{t('common.name')}</dt>
              <dd className="text-right sm:text-left">{data.name}</dd>

              <dt className="text-muted-foreground">{t('customer.customerNumber')}</dt>
              <dd className="text-right sm:text-left font-mono tabular-nums">{formatRef(data.customer_number)}</dd>

              <dt className="text-muted-foreground">{t('common.timezone')}</dt>
              <dd className="text-right sm:text-left">{data.timezone ?? '—'}</dd>

              <dt className="text-muted-foreground">Default country code</dt>
              <dd className="text-right sm:text-left">
                {data.default_country_code
                  ? <span className="font-mono" dir="ltr">{data.default_country_code}</span>
                  : <span className="text-muted-foreground">Reseller default</span>}
              </dd>

              <dt className="text-muted-foreground">{t('common.language')}</dt>
              <dd className="text-right sm:text-left">{data.language.toUpperCase()}</dd>

              <dt className="text-muted-foreground">{t('common.status')}</dt>
              <dd className="text-right sm:text-left">
                <Badge variant={data.active ? 'default' : 'secondary'}>
                  {data.active ? t('common.active') : t('common.inactive')}
                </Badge>
              </dd>

              <dt className="text-muted-foreground">{t('common.createdAt')}</dt>
              <dd className="text-right sm:text-left">{new Date(data.created_at).toLocaleString()}</dd>
            </dl>
          </CardContent>
        </Card>

        <Card>
          <CardHeader>
            <CardTitle className="text-base">{t('common.actions')}</CardTitle>
          </CardHeader>
          <CardContent className="space-y-5 text-sm">
            <div className="space-y-2">
              <p className="font-medium">{t('customer.activation')}</p>
              <Button
                variant="outline"
                size="sm"
                disabled={toggle.isPending}
                onClick={() => toggle.mutate()}
              >
                {toggle.isPending
                  ? (data.active ? t('customer.deactivating') : t('customer.activating'))
                  : (data.active ? t('customer.deactivate') : t('customer.activate'))}
              </Button>
            </div>

            <div className="space-y-2">
              <p className="font-medium">{t('customer.deleteCustomer')}</p>
              <p className="text-xs text-muted-foreground">
                {t('customer.deleteCustomerDescription')}
              </p>
              <Button
                variant="destructive"
                size="sm"
                disabled={del.isPending}
                onClick={() => {
                  if (confirm(t('customer.deleteConfirmMessage', { name: data.name }))) del.mutate();
                }}
              >
                {del.isPending ? t('common.deleting') : t('customer.deleteCustomer')}
              </Button>
              {del.isError && (
                <p role="alert" className="text-xs text-destructive">
                  {del.error instanceof Error ? del.error.message : t('common.failedToDelete')}
                </p>
              )}
            </div>
          </CardContent>
        </Card>
      </div>

      <div className="mt-6">
        <PhoneRecordingSettingsCard customer={data} />
      </div>

      <div className="mt-6">
        <RecordingAdminsCard customerId={id!} />
      </div>

      <div className="mt-6">
        <BackupConfigCard scope="customer" tenantId={id!} />
      </div>

      {(currentUser.role === 'super_admin' || currentUser.role === 'reseller_owner') && (
        <>
          <div className="mt-6">
            <CustomerFeaturesCard customerId={id!} resellerId={data.reseller_id} />
          </div>
          <div className="mt-6">
            <CustomerPricingOverrides customerId={id!} resellerId={data.reseller_id} />
          </div>
        </>
      )}

      <EditCustomerDialog
        open={editOpen}
        onOpenChange={setEditOpen}
        defaults={{
          name: data.name,
          timezone: data.timezone ?? '',
          language: data.language,
          defaultCountryCode: data.default_country_code ?? null,
        }}
        isPending={update.isPending}
        serverError={editError}
        onSubmit={async (v) => {
          setEditError(null);
          try {
            await update.mutateAsync(v);
          } catch (err) {
            setEditError(err instanceof Error ? err.message : t('common.failedToSave'));
          }
        }}
      />
    </>
  );
}

function EditCustomerDialog({
  open, onOpenChange, defaults, onSubmit, isPending, serverError,
}: {
  open: boolean;
  onOpenChange: (v: boolean) => void;
  defaults: EditValues;
  onSubmit: (v: EditValues) => void | Promise<void>;
  isPending: boolean;
  serverError: string | null;
}) {
  const { t } = useTranslation();
  const { register, control, handleSubmit, reset, formState: { errors } } = useForm<EditValues>({
    resolver: zodResolver(editSchema),
    defaultValues: defaults,
  });

  return (
    <Dialog
      open={open}
      onOpenChange={(v) => {
        if (v) reset(defaults);
        onOpenChange(v);
      }}
    >
      <DialogContent>
        <DialogHeader>
          <DialogTitle>{t('customer.editTitle')}</DialogTitle>
          <DialogDescription>
            {t('customer.editDescription')}
          </DialogDescription>
        </DialogHeader>
        <form
          className="space-y-4"
          noValidate
          onSubmit={handleSubmit(onSubmit)}
        >
          <div className="space-y-1">
            <Label htmlFor="edit-name">{t('common.name')}</Label>
            <Input
              id="edit-name"
              aria-invalid={!!errors.name}
              aria-describedby={errors.name ? 'edit-name-error' : undefined}
              {...register('name')}
            />
            {errors.name && (
              <p id="edit-name-error" role="alert" className="text-xs text-destructive">
                {errors.name.message}
              </p>
            )}
          </div>

          <div className="space-y-1">
            <Label>{t('common.timezone')}</Label>
            <Controller
              name="timezone"
              control={control}
              render={({ field }) => (
                <TimezonePicker
                  value={field.value ?? ''}
                  onChange={field.onChange}
                />
              )}
            />
            {errors.timezone && (
              <p id="edit-timezone-error" role="alert" className="text-xs text-destructive">
                {errors.timezone.message}
              </p>
            )}
          </div>

          <div className="space-y-1">
            <Label htmlFor="edit-defaultCountryCode">Default country code</Label>
            <Controller
              name="defaultCountryCode"
              control={control}
              render={({ field }) => (
                <CountryPrefixSelect
                  id="edit-defaultCountryCode"
                  value={field.value ?? null}
                  onChange={field.onChange}
                  inheritLabel="Inherit reseller default"
                />
              )}
            />
            <p className="text-xs text-muted-foreground">
              Phone fields for this customer pre-fill this prefix. Leave on
              inherit to follow the reseller&rsquo;s setting.
            </p>
            {errors.defaultCountryCode && (
              <p role="alert" className="text-xs text-destructive">
                {errors.defaultCountryCode.message}
              </p>
            )}
          </div>

          {/* HIDDEN until he/yi translations ship — Sam 2026-07-02, bring back then
              (the form still submits the account's current language unchanged via
              defaultValues; restore the LANGUAGES const at the top of this file too)
          <div className="space-y-1">
            <Label htmlFor="edit-language">{t('common.language')}</Label>
            <select
              id="edit-language"
              className="flex h-10 w-full rounded-md border border-border bg-background px-3 py-2 text-sm ring-offset-background focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2"
              {...register('language')}
            >
              {LANGUAGES.map((l) => <option key={l.value} value={l.value}>{l.label}</option>)}
            </select>
          </div>
          */}

          {serverError && (
            <p role="alert" className="text-sm text-destructive">{serverError}</p>
          )}

          <DialogFooter className="pt-2 gap-2">
            <Button type="button" variant="outline" onClick={() => onOpenChange(false)}>
              {t('common.cancel')}
            </Button>
            <Button type="submit" disabled={isPending}>
              {isPending ? t('common.saving') : t('common.saveChanges')}
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
}

// PhoneRecordingSettingsCard moved to
// @/components/recordingAdmins/PhoneRecordingSettingsCard so it can be
// reused on the customer-scoped settings page (office_manager-accessible).
// Imported at the top of this file.

// ── Features (per-customer flag overrides) ───────────────────────────────────
// The CANONICAL surface for managing a customer's feature flags. Rendered for
// super_admin / reseller_owner only — customers cannot manage their own flags
// (Sam 2026-08-06; the backend customerMutateGate matches). Each switch shows
// the customer-EFFECTIVE state; flipping it writes a customer override via
// PUT /feature-flags/customer/:key, and "Use reseller default" clears it via
// DELETE. Enabling is blocked when the reseller tier has the feature off
// (mirrors the backend's reseller_must_enable_first rule).

/** Categories split into a secondary "Behavior & storage" group: per-caller
 *  behavior toggles ('Caller') and storage plumbing ('Storage'). They're
 *  still editable here — this page is the only per-customer surface for
 *  them — just visually separated from the sellable modules. */
const ADVANCED_FEATURE_CATEGORIES = new Set(['Caller', 'Storage']);

function groupFeatureRows(rows: EffectiveFeatureRow[]) {
  const map = new Map<string, EffectiveFeatureRow[]>();
  for (const r of rows) {
    if (!map.has(r.category)) map.set(r.category, []);
    map.get(r.category)!.push(r);
  }
  return Array.from(map.entries())
    .map(([category, list]) => ({
      category,
      rows: [...list].sort((a, b) => a.sort_order - b.sort_order),
    }))
    .sort(
      (a, b) =>
        Math.min(...a.rows.map((r) => r.sort_order)) -
        Math.min(...b.rows.map((r) => r.sort_order)),
    );
}

function CustomerFeaturesCard({
  customerId, resellerId,
}: { customerId: string; resellerId: string }) {
  const qc = useQueryClient();
  const me = useCurrentUser();
  const isSA = me.is_super_admin;

  // Customer-effective view (customer override > reseller > owner default).
  const customerQ = useQuery({
    queryKey: ['feature-flags', 'effective', 'customer-admin', customerId],
    queryFn: () =>
      featureFlags.effective({
        customer_id: customerId,
        ...(isSA ? { reseller_id: resellerId } : {}),
      }),
  });
  // Reseller-tier view — what this customer inherits where no override exists,
  // and the ceiling for what can be enabled.
  const resellerQ = useQuery({
    queryKey: ['feature-flags', 'effective', 'reseller-tier', resellerId],
    queryFn: () => featureFlags.effective(isSA ? { reseller_id: resellerId } : {}),
  });

  const rows = (customerQ.data?.rows ?? []).filter((r) => !r.core);
  const resellerEnabled = new Map(
    (resellerQ.data?.rows ?? []).map((r) => [r.key, r.enabled]),
  );

  const moduleGroups = groupFeatureRows(
    rows.filter((r) => !ADVANCED_FEATURE_CATEGORIES.has(r.category)),
  );
  const advancedRows = rows
    .filter((r) => ADVANCED_FEATURE_CATEGORIES.has(r.category))
    .sort((a, b) => a.sort_order - b.sort_order);

  // Prefix invalidation reaches every flag consumer: the nav/gate hook
  // (useFeatureFlag), this card, and the create form's defaults.
  const invalidate = () => qc.invalidateQueries({ queryKey: ['feature-flags'] });

  return (
    <Card>
      <CardHeader>
        <CardTitle className="text-base">Features</CardTitle>
        <p className="text-xs text-muted-foreground mt-1">
          What this customer can use. Switches start on the reseller default;
          changing one saves a per-customer override. Overrides also decide
          which pages appear in the customer's portal and which IVR nodes run.
        </p>
      </CardHeader>
      <CardContent className="p-0">
        {customerQ.isLoading ? (
          <p className="text-sm text-muted-foreground px-6 pb-5">Loading…</p>
        ) : customerQ.isError ? (
          <p role="alert" className="text-sm text-destructive px-6 pb-5">
            {customerQ.error instanceof Error ? customerQ.error.message : 'Failed to load features.'}
          </p>
        ) : (
          <div>
            {moduleGroups.map((g) => (
              <div key={g.category}>
                <div className="px-6 pt-3 pb-1 text-[10px] font-mono uppercase tracking-[0.14em] text-muted-foreground/70">
                  {g.category}
                </div>
                <ul className="divide-y divide-border border-t border-border/60">
                  {g.rows.map((row) => (
                    <FeatureOverrideRow
                      key={row.key}
                      row={row}
                      resellerTierOn={resellerEnabled.get(row.key) ?? row.enabled}
                      customerId={customerId}
                      onChanged={invalidate}
                    />
                  ))}
                </ul>
              </div>
            ))}
            {advancedRows.length > 0 && (
              <div>
                <div className="px-6 pt-3 pb-1 text-[10px] font-mono uppercase tracking-[0.14em] text-muted-foreground/70">
                  Behavior &amp; storage
                </div>
                <ul className="divide-y divide-border border-t border-border/60">
                  {advancedRows.map((row) => (
                    <FeatureOverrideRow
                      key={row.key}
                      row={row}
                      resellerTierOn={resellerEnabled.get(row.key) ?? row.enabled}
                      customerId={customerId}
                      onChanged={invalidate}
                    />
                  ))}
                </ul>
              </div>
            )}
          </div>
        )}
      </CardContent>
    </Card>
  );
}

function FeatureOverrideRow({
  row, resellerTierOn, customerId, onChanged,
}: {
  row: EffectiveFeatureRow;
  /** Whether the feature is on at the reseller tier (the inherit value AND
   *  the precondition for enabling it here). */
  resellerTierOn: boolean;
  customerId: string;
  onChanged: () => void;
}) {
  const [err, setErr] = useState<string | null>(null);
  const hasOverride = row.source === 'customer';

  const setOverride = useMutation({
    mutationFn: (enabled: boolean) =>
      featureFlags.setCustomer(row.key, { enabled, customer_id: customerId }),
    onSuccess: () => { setErr(null); onChanged(); },
    onError: (e) => setErr(e instanceof Error ? e.message : 'Save failed'),
  });
  const clearOverride = useMutation({
    mutationFn: () => featureFlags.clearCustomer(row.key, customerId),
    onSuccess: () => { setErr(null); onChanged(); },
    onError: (e) => setErr(e instanceof Error ? e.message : 'Reset failed'),
  });

  const pending = setOverride.isPending || clearOverride.isPending;
  // Enabling requires the reseller tier to be on (backend enforces
  // reseller_must_enable_first); disabling is always allowed.
  const cannotEnable = !row.enabled && !resellerTierOn;

  return (
    <li className="px-6 py-3 grid grid-cols-[1fr_auto] gap-4 items-start">
      <div className="min-w-0">
        <div className="flex items-center gap-2 flex-wrap">
          <span className="text-sm font-medium">{row.display_name}</span>
          {hasOverride && <Badge variant="default" className="text-[10px]">Override</Badge>}
        </div>
        {row.description && (
          <p className="text-xs text-muted-foreground mt-0.5">{row.description}</p>
        )}
        <p className="text-xs text-muted-foreground mt-1">
          Reseller default:{' '}
          <span className="font-medium text-foreground">{resellerTierOn ? 'On' : 'Off'}</span>
          {cannotEnable && (
            <> — enable it on the reseller's Features page before turning it on here</>
          )}
        </p>
        {err && (
          <p role="alert" className="text-xs text-destructive mt-1">{err}</p>
        )}
      </div>
      <div className="flex flex-col items-end gap-1.5 shrink-0">
        <Switch
          checked={row.enabled}
          disabled={pending || cannotEnable}
          onCheckedChange={(next) => setOverride.mutate(next)}
        />
        {hasOverride && (
          <Button
            size="sm"
            variant="ghost"
            className="h-6 px-2 text-[11px] text-muted-foreground"
            disabled={pending}
            onClick={() => clearOverride.mutate()}
          >
            Use reseller default
          </Button>
        )}
      </div>
    </li>
  );
}

// ── Pricing overrides ────────────────────────────────────────────────────────
// Per-customer markup layer on top of the reseller-wide pricing rules. Each
// row shows the billable item, the reseller-default price (inherited), and
// an optional customer-specific override. Setting an override writes a
// `pricing_rules` row with `customer_id` set; clearing it soft-deletes that
// row so the reseller default reapplies.

function formatMoney(cents: number): string {
  return (cents / 100).toLocaleString(undefined, { style: 'currency', currency: 'USD' });
}

function CustomerPricingOverrides({
  customerId, resellerId,
}: { customerId: string; resellerId: string }) {
  const { t } = useTranslation();
  const qc = useQueryClient();

  const { data: itemsData } = useQuery({
    queryKey: ['billableItems', 'reseller_to_customer'],
    queryFn: () => customBilling.listItems({ scope: 'reseller_to_customer', active_only: true, limit: 500 }),
  });
  // Reseller-wide rules (what the customer inherits).
  const { data: resellerRulesData } = useQuery({
    queryKey: ['pricingRules', 'resellerWide', resellerId],
    queryFn: () => customBilling.listRules({ reseller_id: resellerId, limit: 500 }),
  });
  // Customer-specific overrides.
  const { data: customerRulesData } = useQuery({
    queryKey: ['pricingRules', 'customer', customerId],
    queryFn: () => customBilling.listRules({ customer_id: customerId, limit: 500 }),
  });

  const items = itemsData?.rows ?? [];
  const resellerRules = (resellerRulesData?.rows ?? []).filter((r) => !r.customer_id);
  const customerRules = (customerRulesData?.rows ?? []).filter((r) => r.customer_id === customerId);

  // Only show items the reseller has enabled at the reseller-wide tier —
  // overriding something the reseller isn't charging for would be confusing.
  const enabledItems = items.filter((item) =>
    resellerRules.some((r) => r.billable_item_id === item.id && r.enabled),
  );

  return (
    <Card>
      <CardHeader>
        <CardTitle className="text-base">{t('customer.pricingOverrides')}</CardTitle>
        <p className="text-xs text-muted-foreground mt-1">
          {t('customer.pricingOverridesDescription')}
        </p>
      </CardHeader>
      <CardContent>
        {enabledItems.length === 0 ? (
          <p className="text-sm text-muted-foreground">
            {t('customer.noPricingRulesYet')}
          </p>
        ) : (
          <div className="divide-y divide-border">
            {enabledItems.map((item) => {
              const resellerRule = resellerRules.find((r) => r.billable_item_id === item.id);
              const customerRule = customerRules.find((r) => r.billable_item_id === item.id);
              return (
                <OverrideRow
                  key={item.id}
                  item={item}
                  resellerRule={resellerRule ?? null}
                  customerRule={customerRule ?? null}
                  customerId={customerId}
                  onChanged={() => {
                    qc.invalidateQueries({ queryKey: ['pricingRules', 'customer', customerId] });
                    qc.invalidateQueries({ queryKey: ['pricingRules', 'resellerWide', resellerId] });
                  }}
                />
              );
            })}
          </div>
        )}
      </CardContent>
    </Card>
  );
}

function OverrideRow({
  item, resellerRule, customerRule, customerId, onChanged,
}: {
  item: BillableItemRow;
  resellerRule: PricingRuleRow | null;
  customerRule: PricingRuleRow | null;
  customerId: string;
  onChanged: () => void;
}) {
  const { t } = useTranslation();
  const inheritedCents = resellerRule?.unit_price_cents ?? item.default_unit_price_cents;
  const [draftCents, setDraftCents] = useState(customerRule?.unit_price_cents ?? inheritedCents);
  const [editing, setEditing] = useState(false);

  const save = useMutation({
    mutationFn: () => customBilling.upsertRule({
      billable_item_id: item.id,
      reseller_id: resellerRule?.reseller_id ?? null,
      customer_id: customerId,
      unit_price_cents: draftCents,
      enabled: true,
    }),
    onSuccess: () => { onChanged(); setEditing(false); },
  });
  const clearOverride = useMutation({
    mutationFn: () => customBilling.deleteRule(customerRule!.id),
    onSuccess: () => { onChanged(); setEditing(false); },
  });

  const hasOverride = customerRule !== null;
  const effectiveCents = customerRule?.unit_price_cents ?? inheritedCents;
  const delta = customerRule ? effectiveCents - inheritedCents : 0;

  return (
    <div className="flex items-center justify-between gap-4 py-3">
      <div className="min-w-0 flex-1">
        <div className="flex items-center gap-2">
          <span className="text-sm font-medium">{item.name}</span>
          <Badge variant="outline" className="text-[10px]">{UNIT_KIND_LABELS[item.unit_kind]}</Badge>
          {hasOverride && <Badge variant="default" className="text-[10px]">Override</Badge>}
        </div>
        <div className="text-xs text-muted-foreground mt-0.5 tabular-nums">
          {t('customer.resellerDefault')}: <span className="font-mono">{formatMoney(inheritedCents)}</span>
          {hasOverride && delta !== 0 && (
            <> · {delta > 0 ? '+' : ''}{formatMoney(delta)} vs default</>
          )}
        </div>
      </div>
      <div className="flex items-center gap-2 shrink-0">
        {editing ? (
          <>
            <Input
              type="number" min={0}
              value={draftCents}
              onChange={(e) => setDraftCents(parseInt(e.target.value, 10) || 0)}
              className="w-28 text-sm font-mono"
            />
            <Button size="sm" onClick={() => save.mutate()} disabled={save.isPending}>
              {save.isPending ? t('common.saving') : t('common.save')}
            </Button>
            <Button size="sm" variant="ghost" onClick={() => {
              setDraftCents(customerRule?.unit_price_cents ?? inheritedCents);
              setEditing(false);
            }}>{t('common.cancel')}</Button>
          </>
        ) : (
          <>
            <span className="text-sm tabular-nums font-mono">
              {formatMoney(effectiveCents)}
            </span>
            <Button size="sm" variant="outline" onClick={() => setEditing(true)}>
              {hasOverride ? 'Change' : 'Override'}
            </Button>
            {hasOverride && (
              <Button
                size="sm" variant="ghost" className="text-destructive"
                onClick={() => {
                  if (confirm(`${t('customer.clearOverride')} ${item.name}?`)) {
                    clearOverride.mutate();
                  }
                }}
                disabled={clearOverride.isPending}
              >
                {t('customer.clearOverride')}
              </Button>
            )}
          </>
        )}
      </div>
    </div>
  );
}
